import { GetUtils } from '@iot-platform/iot-platform-utils';
import { BaseState, CommonIndexedPagination, Filter, INITIAL_BASE_STATE } from '@iot-platform/models/common';
import { Receiver, Ward } from '@iot-platform/models/oyan';
import { createEntityAdapter, EntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { SiteWardsActions } from '../actions';
import { withCanRemoveWard } from '../ward-can-remove.helper';

export const featureKey = 'sitesWardsApiFeatureKey';

export type State = BaseState<Ward, CommonIndexedPagination, Filter>;

export const adapter: EntityAdapter<Ward> = createEntityAdapter<Ward>({
  selectId: (entity: Ward) => entity.id,
  sortComparer: false
});

export const initialState: State = adapter.getInitialState({
  ...INITIAL_BASE_STATE,
  pagination: { currentPage: 0, hasMore: false, limit: 10, maxPage: 0, total: 0 }
});

export const reducer = createReducer(
  initialState,

  on(SiteWardsActions.loadWardsBySiteSuccess, (state: State, { response }): State => ({
    ...adapter.setAll(
      GetUtils.get(response, 'data', []).map((ward: Ward) => withCanRemoveWard(ward)),
      { ...state, pagination: response.pagination, selectedId: null, entity: null }
    )
  })),

  on(SiteWardsActions.addReceiverToWardSuccess, (state: State, { ward, receiver }): State => {
    const current: Ward = state.entities[ward.id];
    const receivers: Receiver[] = GetUtils.get(current, 'receivers', []);
    const wardToUpdate: Ward = withCanRemoveWard({ ...current, receivers: [...receivers, receiver] });
    return adapter.updateOne({ id: wardToUpdate.id, changes: wardToUpdate }, { ...state, selectedId: ward.id, entity: wardToUpdate });
  }),

  on(SiteWardsActions.removeReceiverFromWardSuccess, (state: State, { ward, receiver }): State => {
    const current: Ward = state.entities[ward.id];
    const receivers: Receiver[] = GetUtils.get(current, 'receivers', []);
    const wardToUpdate: Ward = withCanRemoveWard({ ...current, receivers: receivers.filter((r) => r.id !== receiver.id) });
    return adapter.updateOne({ id: wardToUpdate.id, changes: wardToUpdate }, { ...state, selectedId: ward.id, entity: wardToUpdate });
  }),

  on(SiteWardsActions.updateWardSuccess, (state: State, { ward }): State => {
    const updated: Ward = withCanRemoveWard(ward);
    return adapter.upsertOne(updated, { ...state, selectedId: updated.id, entity: updated });
  }),

  on(SiteWardsActions.deleteWardSuccess, (state: State, { ward }): State => adapter.removeOne(ward.id, { ...state, selectedId: null, entity: null })),

  on(SiteWardsActions.addThresholdToWardSuccess, (state: State, { ward }): State =>
    adapter.updateOne({ id: ward.id, changes: ward }, { ...state, selectedId: ward.id, entity: ward })
  ),

  on(
    SiteWardsActions.loadWardsBySite,
    SiteWardsActions.addReceiverToWard,
    SiteWardsActions.removeReceiverFromWard,
    SiteWardsActions.addThresholdToWard,
    SiteWardsActions.updateWard,
    SiteWardsActions.deleteWard,
    SiteWardsActions.synchronizeWardsBySite,
    (state: State): State => ({
      ...state,
      loading: true
    })
  ),
  on(
    SiteWardsActions.loadWardsBySiteSuccess,
    SiteWardsActions.addReceiverToWardSuccess,
    SiteWardsActions.removeReceiverFromWardSuccess,
    SiteWardsActions.addThresholdToWardSuccess,
    SiteWardsActions.updateWardSuccess,
    SiteWardsActions.deleteWardSuccess,
    SiteWardsActions.synchronizeWardsBySiteSuccess,
    (state: State): State => ({
      ...state,
      loading: false,
      loaded: true
    })
  ),

  on(
    SiteWardsActions.loadWardsBySiteFailure,
    SiteWardsActions.addReceiverToWardFailure,
    SiteWardsActions.removeReceiverFromWardFailure,
    SiteWardsActions.addThresholdToWardFailure,
    SiteWardsActions.updateWardFailure,
    SiteWardsActions.deleteWardFailure,
    SiteWardsActions.synchronizeWardsBySiteFailure,
    (state: State, { error }): State => ({
      ...state,
      loading: false,
      loaded: false,
      error
    })
  )
);
